import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Trash2, Package, ShoppingBag, Truck, Check, RefreshCw } from "lucide-react";

const STATUSES = ["New", "Confirmed", "Delivered"];

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("New");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/owner/orders"); setOrders(Array.isArray(data) ? data : []); }
    catch { toast.error("Couldn't load orders"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/owner/orders/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Couldn't update status"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this order permanently?")) return;
    try { await api.delete(`/owner/orders/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Couldn't delete"); }
  };

  const filtered = orders.filter((o) => o.status === tab);
  const counts = { New: 0, Confirmed: 0, Delivered: 0 };
  orders.forEach((o) => { if (counts[o.status] !== undefined) counts[o.status]++; });

  return (
    <div data-testid="orders-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-alfa text-xl uppercase">Store Orders ({orders.length})</h3>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
            PayPal-confirmed orders. Move through New → Confirmed → Delivered as you fulfill.
          </p>
        </div>
        <button type="button" onClick={load} data-testid="orders-refresh" className="inline-flex items-center gap-1 border border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bone">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="orders-status-tabs">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setTab(s)}
            data-testid={`orders-tab-${s.toLowerCase()}`}
            className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.24em] ${tab === s ? "border-ink bg-ink text-bone" : "border-ink/40 text-ink hover:bg-ink/5"}`}
          >
            {s} ({counts[s]})
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-4">
        {filtered.length === 0 && (
          <p className="border border-dashed border-ink/40 p-6 text-center font-serif text-sm text-ink/60">
            No {tab.toLowerCase()} orders.
          </p>
        )}
        {filtered.map((o) => (
          <div key={o.id} data-testid={`order-${o.id}`} className="border border-ink bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-alfa text-lg uppercase leading-tight">{o.payer_name || "Guest"}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{o.payer_email}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
                  {fmtDate(o.created_at)} · PayPal #{o.paypal_order_id?.slice(-8) || "—"}
                </p>
              </div>
              <div className="text-right">
                <p className="font-alfa text-xl">${o.total.toFixed(2)}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
                  Items ${o.subtotal.toFixed(2)} · Ship ${o.shipping.toFixed(2)}{o.tax ? ` · Tax $${o.tax.toFixed(2)}` : ""}
                </p>
              </div>
            </div>
            <ul className="mt-3 space-y-1 border-t border-ink/20 pt-3">
              {o.items.map((it, i) => (
                <li key={i} className="flex justify-between font-mono text-xs">
                  <span>{it.qty}× {it.name}{it.variant ? ` (${it.variant})` : ""}</span>
                  <span>${(it.price * it.qty).toFixed(2)}</span>
                </li>
              ))}
            </ul>
            {o.shipping_address?.address && (
              <div className="mt-3 border-t border-ink/20 pt-3 font-mono text-xs">
                <p className="text-ink/60">Ship to:</p>
                <p>{o.shipping_address.name?.full_name || o.payer_name}</p>
                <p>{o.shipping_address.address.address_line_1}</p>
                {o.shipping_address.address.address_line_2 && <p>{o.shipping_address.address.address_line_2}</p>}
                <p>{o.shipping_address.address.admin_area_2}, {o.shipping_address.address.admin_area_1} {o.shipping_address.address.postal_code}</p>
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {o.status !== "New" && (
                <button type="button" onClick={() => setStatus(o.id, "New")} data-testid={`order-${o.id}-set-new`} className="inline-flex items-center gap-1 border border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bone">
                  <ShoppingBag size={12} /> New
                </button>
              )}
              {o.status !== "Confirmed" && (
                <button type="button" onClick={() => setStatus(o.id, "Confirmed")} data-testid={`order-${o.id}-set-confirmed`} className="inline-flex items-center gap-1 border border-ink bg-ink/90 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-bone hover:bg-ink">
                  <Check size={12} /> Mark Confirmed
                </button>
              )}
              {o.status !== "Delivered" && (
                <button type="button" onClick={() => setStatus(o.id, "Delivered")} data-testid={`order-${o.id}-set-delivered`} className="inline-flex items-center gap-1 border-2 border-ink bg-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-bone hover:bg-transparent hover:text-ink">
                  <Truck size={12} /> Mark Delivered
                </button>
              )}
              <button type="button" onClick={() => remove(o.id)} data-testid={`order-${o.id}-delete`} className="ml-auto inline-flex items-center gap-1 border border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-red-700 hover:bg-red-700 hover:text-bone">
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AbandonedCartsPanel() {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/owner/abandoned-carts"); setCarts(Array.isArray(data) ? data : []); }
    catch { toast.error("Couldn't load abandoned carts"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Dismiss this abandoned cart?")) return;
    try { await api.delete(`/owner/abandoned-carts/${id}`); toast.success("Dismissed"); load(); }
    catch { toast.error("Couldn't dismiss"); }
  };

  return (
    <div data-testid="abandoned-carts-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-alfa text-xl uppercase">Abandoned Carts ({carts.length})</h3>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
            Customers who opened PayPal but cancelled or errored out. Dismiss when handled.
          </p>
        </div>
        <button type="button" onClick={load} data-testid="carts-refresh" className="inline-flex items-center gap-1 border border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bone">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mt-4 space-y-4">
        {carts.length === 0 && (
          <p className="border border-dashed border-ink/40 p-6 text-center font-serif text-sm text-ink/60">
            No abandoned carts. 🎉
          </p>
        )}
        {carts.map((c) => (
          <div key={c.id} data-testid={`cart-${c.id}`} className="border border-red-700/60 bg-red-50/40 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-alfa text-lg uppercase leading-tight">
                  <Package size={14} className="inline" /> {c.reason === "error" ? "Payment Failed" : "Cart Abandoned"}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{fmtDate(c.created_at)}</p>
              </div>
              <p className="font-alfa text-xl">${c.total.toFixed(2)}</p>
            </div>
            <ul className="mt-3 space-y-1 border-t border-ink/20 pt-3">
              {c.items.map((it, i) => (
                <li key={i} className="flex justify-between font-mono text-xs">
                  <span>{it.qty}× {it.name}{it.variant ? ` (${it.variant})` : ""}</span>
                  <span>${(it.price * it.qty).toFixed(2)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4">
              <button type="button" onClick={() => remove(c.id)} data-testid={`cart-${c.id}-dismiss`} className="inline-flex items-center gap-1 border border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bone">
                <Trash2 size={12} /> Dismiss
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
